/**
 * CaraDrawer — the docked, in-place Ask Cara surface.
 *
 * Lives as a floating pill at the bottom-right of every authenticated
 * dashboard. Tapping the pill opens a right-edge slide-in drawer
 * (420px on desktop, full-width on mobile) that runs the full Ask Cara
 * chat experience next to the user's work — no page navigation.
 *
 * Architecture:
 *   - Reuses the same `/api/cara/chat[-public]` backend (Vertex BAA path).
 *   - Reuses the same `/api/cara/meta` for role catalog + greetings.
 *   - Honors `sessionStorage.cara_prefilled_prompt` so a tap on a Cara
 *     nudge tile auto-opens the drawer with the prompt in the input.
 *   - Live vs Sample mode badge auto-flips based on the active tab vs
 *     the signed-in user's role.
 *
 * Brand: pure `cn-*` CSS variables → light/dark mode follows the
 * platform shell automatically.
 *
 * data-testids: cara-drawer-pill, cara-drawer, cara-drawer-close,
 * cara-drawer-role-{key}, cara-drawer-input, cara-drawer-send,
 * cara-drawer-suggestion-{i}, cara-drawer-message-{i},
 * cara-drawer-loading, cara-drawer-mode-badge, cara-drawer-open-full.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, Sparkles, X, Maximize2, MessageCircle, RotateCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { resolveCaraRole } from '../../lib/cara_roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes where Cara has nothing useful to add and would feel intrusive
// (auth pages, the standalone /ask-cara page, public marketing pages).
const HIDDEN_ROUTES = [/^\/auth/, /^\/ask-cara$/, /^\/$/, /^\/pricing/, /^\/about/];

function CaraMark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" aria-hidden="true">
      <g fill="none">
        <path d="M4 6 V20 M9 6 V20 M14 6 V20 M19 6 V20"
              stroke="var(--cn-text-muted)" strokeWidth="1.1" />
        <path d="M2 9 C7 9 7 13 12 13 S17 17 24 17"
              stroke="var(--cn-accent)" strokeWidth="1.8" />
      </g>
    </svg>
  );
}

export function CaraDrawer() {
  const { user, getAuthHeader } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthed = !!user;
  const userRole = resolveCaraRole(user?.role);
  const hidden = !isAuthed || HIDDEN_ROUTES.some((re) => re.test(location.pathname));

  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [role, setRole] = useState(userRole || 'planner');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Sync `role` to `userRole` once when the auth context finishes
  // loading `user`. Prevents Cara from staying pinned to the fallback
  // 'planner' tab (→ forced sample mode) after login. See
  // /app/frontend/src/pages/AskCara.jsx for the same pattern.
  const roleSynced = useRef(false);
  useEffect(() => {
    if (userRole && !roleSynced.current) {
      setRole(userRole);
      roleSynced.current = true;
    }
  }, [userRole]);

  // Live mode only when the active tab matches the user's real role.
  const mode = isAuthed && userRole && role === userRole ? 'live' : 'sample';
  const endpoint = mode === 'live' ? `${API}/cara/chat` : `${API}/cara/chat-public`;

  // Load static meta once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cara/meta`);
        if (!cancelled) {
          setMeta(r.data);
          const cfg = r.data.roles.find((x) => x.key === (userRole || 'planner'));
          if (cfg) setMessages([{ who: 'cara', text: cfg.greeting }]);
        }
      } catch {
        if (!cancelled) setMeta({ roles: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [userRole]);

  // Persistence: when the drawer first opens (or the role tab changes
  // while open), restore the user's recent Cara turns from the last
  // 24h so the conversation picks up where they left off. Falls back
  // gracefully on any API error — the drawer still shows the canonical
  // greeting and works as a fresh thread.
  useEffect(() => {
    if (!open || !isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = getAuthHeader()?.headers || {};
        const { data } = await axios.get(`${API}/cara/recent`, {
          params: { role, limit: 8 },
          headers,
        });
        if (cancelled) return;
        const turns = data?.turns || [];
        if (turns.length === 0) return; // keep the canonical greeting
        const restored = [{ who: 'cara', text: '— Picking up where we left off —' }];
        turns.forEach((t) => {
          if (t.user) restored.push({ who: 'user', text: t.user });
          if (t.assistant) restored.push({ who: 'cara', text: t.assistant });
        });
        setMessages(restored);
      } catch { /* fail-soft: keep the fresh-greeting state */ }
    })();
    return () => { cancelled = true; };
  }, [open, role, isAuthed, getAuthHeader]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  // If a Cara nudge stashed a prefilled prompt, auto-open the drawer +
  // surface it in the input. This is the same handshake AskCara honors
  // for users who arrived at the standalone page from a nudge.
  useEffect(() => {
    try {
      const prefilled = sessionStorage.getItem('cara_prefilled_prompt');
      if (prefilled && !hidden) {
        setInput(prefilled);
        sessionStorage.removeItem('cara_prefilled_prompt');
        setOpen(true);
      }
    } catch { /* sessionStorage unavailable */ }
  }, [hidden, location.pathname]);

  // Listen for `cara:open` — fired by CaraNudges (and any future
  // surface that wants to open the drawer). The detail.prompt is
  // surfaced in the input; the user reviews and sends. We don't
  // auto-send so the user has a consent step before talking to Cara.
  useEffect(() => {
    if (hidden) return undefined;
    const onOpen = (e) => {
      const promptText = e?.detail?.prompt || '';
      if (promptText) setInput(promptText);
      setOpen(true);
    };
    window.addEventListener('cara:open', onOpen);
    return () => window.removeEventListener('cara:open', onOpen);
  }, [hidden]);

  // Focus the input when the drawer opens (small UX delight).
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const switchRole = useCallback((r) => {
    setRole(r);
    setInput('');
    const cfg = meta?.roles?.find((x) => x.key === r);
    setMessages(cfg ? [{ who: 'cara', text: cfg.greeting }] : []);
  }, [meta]);

  // Clear persisted history for this (user, role) and reset to a fresh
  // greeting. Fail-soft on API error — we still reset the UI so the
  // user gets a clean canvas.
  const newThread = useCallback(async () => {
    if (isAuthed) {
      try {
        const headers = getAuthHeader()?.headers || {};
        await axios.delete(`${API}/cara/recent`, { params: { role }, headers });
      } catch { /* fail-soft */ }
    }
    const cfg = meta?.roles?.find((x) => x.key === role);
    setMessages(cfg ? [{ who: 'cara', text: cfg.greeting }] : []);
    setInput('');
  }, [role, isAuthed, getAuthHeader, meta]);

  const send = useCallback(async (override) => {
    const content = (override ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { who: 'user', text: content }];
    setMessages(next);
    setInput('');
    setLoading(true);

    const apiMessages = next
      .map((m) => ({ role: m.who === 'user' ? 'user' : 'assistant', content: m.text }))
      .filter((_, i) => !(i === 0 && next[0].who === 'cara'));

    try {
      const headers = mode === 'live' ? (getAuthHeader()?.headers || {}) : {};
      const { data } = await axios.post(endpoint, {
        messages: apiMessages, role, mode,
      }, { headers });
      setMessages((m) => [...m, { who: 'cara', text: data.reply || "Sorry — I couldn't generate a reply. Try again?" }]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 503) msg = "Cara's BAA-locked AI path is temporarily unavailable. Please retry in a moment.";
      else if (typeof detail === 'string') msg = detail;
      else msg = "I hit a connection error. Please try again.";
      setMessages((m) => [...m, { who: 'cara', text: msg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, mode, endpoint, role, getAuthHeader]);

  if (hidden) return null;

  const activeRoleCfg = meta?.roles?.find((x) => x.key === role) || {
    label: '—', eyebrow: '', greeting: '', suggestions: [],
  };
  const showSuggestions = messages.length <= 1 && activeRoleCfg.suggestions?.length;
  const isLive = mode === 'live';

  // Role-tab visibility mirrors AskCara.jsx (iter133):
  //   • signed-in admin → all 6 tabs (cross-role visibility)
  //   • anyone else     → only their own tab (never surfaces
  //                        sample-mode-that-looks-like-a-bug)
  //   The Drawer is auth-gated (`isAuthed` required to render), so the
  //   unauthenticated branch used by AskCara.jsx doesn't apply here.
  const isAdmin = userRole === 'admin';
  const visibleRoles = (meta?.roles || []).filter((r) => {
    if (isAdmin) return true;
    return r.key === userRole;
  });

  return (
    <>
      {/* Floating pill — bottom-right */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="cara-drawer-pill"
          aria-label="Open Ask Cara"
          className="fixed bottom-6 right-6 z-[60] rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 pl-2 pr-4 py-2"
          style={{
            background: 'var(--cn-surface)',
            border: '1px solid var(--cn-border)',
            color: 'var(--cn-text)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cn-accent)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--cn-border)'; }}
        >
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'var(--cn-primary)' }}
          >
            <CaraMark size={18} />
          </span>
          <span className="text-sm font-semibold">Ask Cara</span>
          <span
            className="ml-1 w-2 h-2 rounded-full"
            style={{ background: isLive ? '#34D399' : 'var(--cn-accent)' }}
            aria-hidden="true"
          />
        </button>
      )}

      {/* Backdrop (mobile only) */}
      {open && (
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[55] md:hidden"
          style={{ background: 'rgba(15,23,42,0.35)' }}
        />
      )}

      {/* Drawer */}
      <div
        data-testid="cara-drawer"
        role="dialog"
        aria-label="Ask Cara"
        aria-hidden={!open}
        className="fixed inset-y-0 right-0 z-[60] flex flex-col w-full md:w-[420px] transition-transform duration-300"
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          background: 'var(--cn-surface)',
          borderLeft: '1px solid var(--cn-border)',
          boxShadow: open ? '-12px 0 40px -8px rgba(15,23,42,0.18)' : 'none',
        }}
      >
        {/* Header */}
        <div
          className="px-4 py-3 flex flex-col gap-3 shrink-0"
          style={{ background: 'var(--cn-primary)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(6,182,212,0.12)' }}
            >
              <CaraMark size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold leading-none text-base">Ask Cara</div>
              <div className="text-[11px] mt-1 tracking-wide truncate" style={{ color: 'var(--cn-accent)' }}>
                {activeRoleCfg.eyebrow}
              </div>
            </div>
            <span
              data-testid="cara-drawer-mode-badge"
              className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border whitespace-nowrap"
              style={{
                color: isLive ? '#34D399' : 'var(--cn-accent)',
                borderColor: isLive ? 'rgba(52,211,153,0.4)' : 'rgba(6,182,212,0.4)',
                background: isLive ? 'rgba(52,211,153,0.08)' : 'rgba(6,182,212,0.08)',
              }}
            >
              {isLive ? 'Live · your data' : 'Demo · sample'}
            </span>
            <button
              type="button"
              onClick={newThread}
              data-testid="cara-drawer-new-thread"
              aria-label="Start a new thread"
              title="Start a new thread"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/ask-cara'); }}
              data-testid="cara-drawer-open-full"
              aria-label="Open as full page"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="cara-drawer-close"
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {visibleRoles.map((val) => {
              const active = role === val.key;
              return (
                <button
                  key={val.key}
                  onClick={() => switchRole(val.key)}
                  data-testid={`cara-drawer-role-${val.key}`}
                  className="text-[11px] px-2.5 py-1 rounded-full transition"
                  style={active ? {
                    background: 'var(--cn-accent)',
                    color: 'var(--cn-primary)',
                    fontWeight: 600,
                  } : {
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.72)',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  {val.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          style={{ background: 'var(--cn-bg)' }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`cara-drawer-message-${i}`}
              className={`flex ${m.who === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {m.who === 'cara' && (
                <div
                  className="mr-2 mt-1 shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--cn-primary)' }}
                >
                  <CaraMark size={13} />
                </div>
              )}
              <div
                className="max-w-[80%] px-3.5 py-2 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap"
                style={m.who === 'user' ? {
                  background: 'var(--cn-primary)',
                  color: 'var(--cn-surface)',
                  borderBottomRightRadius: 4,
                } : {
                  background: 'var(--cn-surface)',
                  color: 'var(--cn-text)',
                  border: '1px solid var(--cn-border)',
                  borderBottomLeftRadius: 4,
                }}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="cara-drawer-loading">
              <div
                className="mr-2 mt-1 shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'var(--cn-primary)' }}
              >
                <CaraMark size={13} />
              </div>
              <div
                className="rounded-2xl px-3.5 py-2.5"
                style={{
                  background: 'var(--cn-surface)',
                  border: '1px solid var(--cn-border)',
                  borderBottomLeftRadius: 4,
                }}
              >
                <div className="flex gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--cn-text-muted)', animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Suggestions */}
        {showSuggestions && (
          <div className="px-4 pt-2 pb-1 flex flex-wrap gap-1.5 shrink-0" style={{ background: 'var(--cn-bg)' }}>
            {activeRoleCfg.suggestions.slice(0, 3).map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`cara-drawer-suggestion-${i}`}
                className="text-[11.5px] rounded-full px-2.5 py-1 transition inline-flex items-center gap-1.5"
                style={{
                  background: 'var(--cn-surface)',
                  color: 'var(--cn-text)',
                  border: '1px solid var(--cn-border)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cn-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--cn-border)'; }}
              >
                <Sparkles className="w-3 h-3" style={{ color: 'var(--cn-accent)' }} />
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-3 py-3 shrink-0" style={{ background: 'var(--cn-bg)', borderTop: '1px solid var(--cn-border)' }}>
          <div
            className="flex items-center gap-2 rounded-full pl-3.5 pr-1.5 py-1.5"
            style={{ background: 'var(--cn-surface)', border: '1px solid var(--cn-border)' }}
          >
            <input
              ref={inputRef}
              data-testid="cara-drawer-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder={`Ask Cara as ${activeRoleCfg.label.toLowerCase()}…`}
              className="flex-1 outline-none text-[13.5px] bg-transparent min-w-0"
              style={{ color: 'var(--cn-text)' }}
            />
            <button
              data-testid="cara-drawer-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40 transition"
              style={{ background: 'var(--cn-accent)', color: 'var(--cn-primary)' }}
              aria-label="Send"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-[10px] text-center mt-2 px-1" style={{ color: 'var(--cn-text-muted)' }}>
            {isLive
              ? 'BAA-covered AI path. Audited. Cara doesn\u2019t make clinical, coding, or credentialing decisions.'
              : 'Demo · sample data. Switch to your role tab for live mode.'}
          </div>
        </div>
      </div>
    </>
  );
}

export default CaraDrawer;
